import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useAuth } from './useAuth'
import {
  CreateRequisition,
  FinalizeRequisition,
  SampleCompromised,
  UpdateRequisitionFollowUp
} from '../index'
import { throwOnError } from '../utils/throwOnError'

interface FinalizeRequisitionMutationVariables {
  requisitionId: number
  etag: string
  body: FinalizeRequisition
}

interface UpdateRequisitionFollowUpMutationVariables {
  requisitionId: number
  etag: string
  body: UpdateRequisitionFollowUp
}

export const useCreateRequisitionMutation = () => {
  const queryClient = useQueryClient()
  const { authFetchV3 } = useAuth()
  return useMutation({
    mutationFn: async (requisition: CreateRequisition) => {
      const { data, error } = await authFetchV3().POST('/requisitions', {
        body: requisition
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['requisitions']
      })
    }
  })
}

export const useGetRequisitionByIdQuery = (requisitionId?: number) => {
  const { authFetchV3 } = useAuth()
  return useQuery({
    queryKey: ['requisitions', requisitionId],
    queryFn: requisitionId
      ? async () => {
          const { data, error } = await authFetchV3().GET(
            '/requisitions/{requisitionId}',
            {
              headers: {
                Accept: 'application/vnd.mattilsynet.proveta.v3+json'
              },
              params: {
                path: {
                  requisitionId
                }
              }
            }
          )
          if (error) {
            throwOnError(error)
          }
          return data
        }
      : skipToken
  })
}

export const useFinalizeRequisitionMutation = () => {
  const queryClient = useQueryClient()
  const { authFetchV3 } = useAuth()
  return useMutation({
    mutationFn: async ({
      requisitionId,
      etag,
      body
    }: FinalizeRequisitionMutationVariables) => {
      const { data, error } = await authFetchV3().POST(
        '/requisitions/{requisitionId}/finalize',
        {
          params: {
            header: {
              'If-Match': etag
            },
            path: {
              requisitionId
            }
          },
          body
        }
      )
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ['requisitions', data.id]
      })
      await queryClient.invalidateQueries({
        queryKey: ['samples', data.sample?.id]
      })
    }
  })
}

export const useDeleteRequisitionMutation = (sampleId?: string) => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      requisitionId,
      etag
    }: {
      requisitionId: number
      etag: string
    }) =>
      await authFetch().DELETE('/requisitions/{requisitionId}', {
        params: {
          header: {
            'If-Match': etag
          },
          path: { requisitionId }
        }
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['requisitions']
      })
      await queryClient.invalidateQueries({
        queryKey: ['samples', sampleId]
      })
    }
  })
}

export const useSampleCompromisedMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      requisitionId,
      body
    }: {
      requisitionId: number
      body: SampleCompromised
    }) => {
      const { data, error } = await authFetch().POST(
        '/requisitions/{requisitionId}/sample-compromised',
        {
          params: {
            path: {
              requisitionId
            }
          },
          body
        }
      )
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['requisitions'] }),
        queryClient.invalidateQueries({ queryKey: ['samples'] })
      ])
    }
  })
}

export const useUpdateRequisitionFollowupMutation = () => {
  const queryClient = useQueryClient()
  const { authFetchV3 } = useAuth()
  return useMutation({
    mutationFn: async ({
      requisitionId,
      etag,
      body
    }: UpdateRequisitionFollowUpMutationVariables) => {
      const { data, error } = await authFetchV3().PATCH(
        '/requisitions/{requisitionId}/follow-up',
        {
          params: {
            header: {
              'If-Match': etag
            },
            path: {
              requisitionId
            }
          },
          body
        }
      )
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ['requisitions', data.id]
      })
      await queryClient.invalidateQueries({
        queryKey: ['samples', data.sample?.id]
      })
    }
  })
}
